import { api } from "../api";
import type { FeedbackType } from "./feedback";

export interface FeedbackEntry {
  id: string;
  user_id: string;
  email: string;
  feedback_text: string;
  feedback_type: FeedbackType;
  created_at: string;
  updated_at: string;
}

export interface FeedbackListResponse {
  entries: FeedbackEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface FeedbackListParams {
  password: string;
  feedback_type?: FeedbackType;
  email?: string;
  limit?: number;
  offset?: number;
}

export const listFeedback = async (
  params: FeedbackListParams
): Promise<FeedbackListResponse> => {
  const { password, ...queryParams } = params;
  
  const searchParams = new URLSearchParams();
  if (queryParams.feedback_type) searchParams.set("feedback_type", queryParams.feedback_type);
  if (queryParams.email) searchParams.set("email", queryParams.email);
  if (queryParams.limit) searchParams.set("limit", queryParams.limit.toString());
  if (queryParams.offset) searchParams.set("offset", queryParams.offset.toString());

  const queryString = searchParams.toString();
  const url = queryString ? `api/dev/feedback?${queryString}` : "api/dev/feedback";

  return api.get(url, {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<FeedbackListResponse>();
};

export const verifyDevPassword = async (password: string): Promise<{ valid: boolean }> => {
  return api.post("api/dev/verify-password", {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<{ valid: boolean }>();
};

export interface ErrorLogEntry {
  id: string;
  error_type: string;
  error_message: string;
  stack_trace?: string;
  request_method?: string;
  request_path?: string;
  request_query?: string;
  request_headers?: Record<string, any>;
  request_body?: Record<string, any>;
  user_id?: string;
  user_email?: string;
  correlation_id?: string;
  request_id?: string;
  environment: string;
  service?: string;
  operation?: string;
  error_details?: Record<string, any>;
  created_at: string;
}

export interface ErrorLogListResponse {
  entries: ErrorLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface ErrorLogListParams {
  password: string;
  user_id?: string;
  user_email?: string;
  error_type?: string;
  limit?: number;
  offset?: number;
}

export const listErrors = async (
  params: ErrorLogListParams
): Promise<ErrorLogListResponse> => {
  const { password, ...queryParams } = params;
  
  const searchParams = new URLSearchParams();
  if (queryParams.user_id) searchParams.set("user_id", queryParams.user_id);
  if (queryParams.user_email) searchParams.set("user_email", queryParams.user_email);
  if (queryParams.error_type) searchParams.set("error_type", queryParams.error_type);
  if (queryParams.limit) searchParams.set("limit", queryParams.limit.toString());
  if (queryParams.offset) searchParams.set("offset", queryParams.offset.toString());

  const queryString = searchParams.toString();
  const url = queryString ? `api/dev/errors?${queryString}` : "api/dev/errors";

  return api.get(url, {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<ErrorLogListResponse>();
};

export interface UserUsageStats {
  user_id?: string;
  user_email?: string;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  request_count: number;
}

export interface UsageStatsResponse {
  users: UserUsageStats[];
  totals: {
    total_tokens: number;
    prompt_tokens: number;
    completion_tokens: number;
    request_count: number;
  };
  total_users: number;
}

export const getUsageStats = async (
  password: string
): Promise<UsageStatsResponse> => {
  return api.get("api/dev/usage", {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<UsageStatsResponse>();
};

export interface UploadEntry {
  source: string;
  display_name: string;
  is_legacy: boolean;
  is_user_scoped: boolean;
  user_id?: string;
  user_email?: string;
  file_size: number;
  file_exists: boolean;
}

export interface UserUploadStats {
  user_id: string;
  user_email?: string;
  upload_count: number;
  total_size: number;
}

export interface UploadListResponse {
  entries: UploadEntry[];
  total: number;
  legacy_count: number;
  user_scoped_count: number;
  by_user: UserUploadStats[];
  total_size: number;
}

export interface UserOverviewEntry {
  user_id: string;
  user_email?: string;
  created_at?: string;
  last_active?: string;
  total_tokens: number;
  prompt_tokens: number;
  completion_tokens: number;
  request_count: number;
  upload_count: number;
  upload_size: number;
  conversation_count: number;
  feedback_count: number;
}

export interface UserOverviewResponse {
  users: UserOverviewEntry[];
  total_users: number;
  totals: {
    total_tokens: number;
    prompt_tokens: number;
    completion_tokens: number;
    request_count: number;
    upload_count: number;
    upload_size: number;
    conversation_count: number;
    feedback_count: number;
  };
}

export interface DeleteUploadResponse {
  success: boolean;
  rows_affected: number;
  files_deleted: number;
  message: string;
  error?: string;
}

export const listUploads = async (
  password: string
): Promise<UploadListResponse> => {
  return api.get("api/dev/uploads", {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<UploadListResponse>();
};

export interface ClearLegacyResponse {
  deleted_sources: number;
  deleted_files: number;
  message: string;
}

export interface ClearLegacyParams {
  password: string;
  include_unindexed?: boolean;
}

export const clearLegacyUploads = async (
  params: ClearLegacyParams
): Promise<ClearLegacyResponse> => {
  const { password, include_unindexed } = params;
  const searchParams = new URLSearchParams();
  if (include_unindexed) {
    searchParams.set("include_unindexed", "true");
  }
  const queryString = searchParams.toString();
  const url = queryString ? `api/dev/uploads/clear-legacy?${queryString}` : "api/dev/uploads/clear-legacy";
  
  return api.post(url, {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<ClearLegacyResponse>();
};

export interface DeleteUploadParams {
  password: string;
  source: string;
}

export const deleteUpload = async (
  params: DeleteUploadParams
): Promise<DeleteUploadResponse> => {
  const { password, source } = params;
  // Use the dedicated upload delete endpoint
  return api.post("api/dev/uploads/delete", {
    headers: {
      "X-Dev-Password": password,
    },
    json: {
      source,
    },
  }).json<DeleteUploadResponse>();
};

export const getUsersOverview = async (
  password: string
): Promise<UserOverviewResponse> => {
  return api.get("api/dev/users/overview", {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<UserOverviewResponse>();
};

export interface OrganizationStatsEntry {
  org_id: string;
  name?: string;
  plan?: string;
  created_at?: string;
  member_count: number;
  project_count: number;
  tokens_this_month: number;
  total_tokens: number;
  upload_count: number;
  upload_size: number;
}

export interface OrganizationsStatsResponse {
  organizations: OrganizationStatsEntry[];
  total_organizations: number;
  totals: {
    total_members: number;
    total_projects: number;
    total_tokens_this_month: number;
    total_tokens_all_time: number;
    total_uploads: number;
    total_upload_size: number;
  };
}

export const getOrganizationsStats = async (
  password: string
): Promise<OrganizationsStatsResponse> => {
  return api.get("api/dev/organizations", {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<OrganizationsStatsResponse>();
};

// Database Management Types
export interface TableInfo {
  name: string;
  type: string;
  sql?: string;
}

export interface DatabaseStats {
  database_path: string;
  database_size_bytes: number;
  tables: TableInfo[];
  total_rows: number;
  table_row_counts: Record<string, number>;
}

export interface TableSchema {
  table_name: string;
  columns: Array<{
    cid: number;
    name: string;
    type: string;
    notnull: boolean;
    dflt_value: any;
    pk: boolean;
  }>;
  indexes: Array<{
    name: string;
    unique: boolean;
    columns: string[];
  }>;
  row_count: number;
}

export interface TableDataResponse {
  columns: string[];
  rows: any[][];
  total_rows: number;
  page: number;
  page_size: number;
}

export interface QueryRequest {
  query: string;
  limit?: number;
}

export interface QueryResponse {
  columns: string[];
  rows: any[][];
  row_count: number;
  execution_time_ms: number;
  error?: string;
}

export interface DatabaseExport {
  database_path: string;
  exported_at: string;
  tables: Record<string, {
    columns: string[];
    rows: any[];
    row_count: number;
  }>;
}

export interface DatabaseInfo {
  path: string;
  name: string;
  size_bytes: number;
  is_current: boolean;
}

export interface DatabaseListResponse {
  databases: DatabaseInfo[];
  current_database: string;
}

export interface UpdateRequest {
  table: string;
  set_values: Record<string, any>;
  where: Record<string, any>;
}

export interface DeleteRequest {
  table: string;
  where: Record<string, any>;
}

export interface WriteResponse {
  success: boolean;
  rows_affected: number;
  message: string;
  error?: string;
}

export const listDatabases = async (
  password: string
): Promise<DatabaseListResponse> => {
  return api.get("api/dev/database/list", {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<DatabaseListResponse>();
};

export const selectDatabase = async (
  password: string,
  databasePath: string
): Promise<{ success: boolean; database_path: string; message: string }> => {
  return api.post(`api/dev/database/select?database_path=${encodeURIComponent(databasePath)}`, {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<{ success: boolean; database_path: string; message: string }>();
};

export const getDatabaseStats = async (
  password: string,
  databasePath?: string
): Promise<DatabaseStats> => {
  const searchParams = new URLSearchParams();
  if (databasePath) {
    searchParams.set("database_path", databasePath);
  }
  const queryString = searchParams.toString();
  const url = queryString ? `api/dev/database/stats?${queryString}` : "api/dev/database/stats";
  return api.get(url, {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<DatabaseStats>();
};

export const getTableSchema = async (
  password: string,
  tableName: string,
  databasePath?: string
): Promise<TableSchema> => {
  const searchParams = new URLSearchParams();
  if (databasePath) {
    searchParams.set("database_path", databasePath);
  }
  const queryString = searchParams.toString();
  const url = queryString 
    ? `api/dev/database/tables/${encodeURIComponent(tableName)}/schema?${queryString}`
    : `api/dev/database/tables/${encodeURIComponent(tableName)}/schema`;
  return api.get(url, {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<TableSchema>();
};

export const getTableData = async (
  password: string,
  tableName: string,
  page: number = 1,
  pageSize: number = 50,
  databasePath?: string
): Promise<TableDataResponse> => {
  const searchParams = new URLSearchParams();
  searchParams.set("page", page.toString());
  searchParams.set("page_size", pageSize.toString());
  if (databasePath) {
    searchParams.set("database_path", databasePath);
  }
  return api.get(`api/dev/database/tables/${encodeURIComponent(tableName)}/data?${searchParams}`, {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<TableDataResponse>();
};

export const executeQuery = async (
  password: string,
  request: QueryRequest,
  databasePath?: string
): Promise<QueryResponse> => {
  const searchParams = new URLSearchParams();
  if (databasePath) {
    searchParams.set("database_path", databasePath);
  }
  const queryString = searchParams.toString();
  const url = queryString ? `api/dev/database/query?${queryString}` : "api/dev/database/query";
  return api.post(url, {
    headers: {
      "X-Dev-Password": password,
    },
    json: request,
  }).json<QueryResponse>();
};

export const updateRows = async (
  password: string,
  request: UpdateRequest,
  databasePath?: string
): Promise<WriteResponse> => {
  const searchParams = new URLSearchParams();
  if (databasePath) {
    searchParams.set("database_path", databasePath);
  }
  const queryString = searchParams.toString();
  const url = queryString ? `api/dev/database/update?${queryString}` : "api/dev/database/update";
  return api.post(url, {
    headers: {
      "X-Dev-Password": password,
    },
    json: request,
  }).json<WriteResponse>();
};

export const deleteRows = async (
  password: string,
  request: DeleteRequest,
  databasePath?: string
): Promise<WriteResponse> => {
  const searchParams = new URLSearchParams();
  if (databasePath) {
    searchParams.set("database_path", databasePath);
  }
  const queryString = searchParams.toString();
  const url = queryString ? `api/dev/database/delete?${queryString}` : "api/dev/database/delete";
  return api.post(url, {
    headers: {
      "X-Dev-Password": password,
    },
    json: request,
  }).json<WriteResponse>();
};

export const exportDatabase = async (
  password: string,
  format: "json" | "csv" = "json"
): Promise<DatabaseExport> => {
  return api.get(`api/dev/database/export?format=${format}`, {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<DatabaseExport>();
};

export interface ClearTableResponse {
  success: boolean;
  rows_deleted: number;
  table_name: string;
  message: string;
  error?: string;
}export const clearTable = async (
  password: string,
  tableName: string,
  databasePath?: string
): Promise<ClearTableResponse> => {
  const searchParams = new URLSearchParams();
  if (databasePath) {
    searchParams.set("database_path", databasePath);
  }
  const queryString = searchParams.toString();
  const url = queryString 
    ? `api/dev/database/tables/${encodeURIComponent(tableName)}/clear?${queryString}`
    : `api/dev/database/tables/${encodeURIComponent(tableName)}/clear`;
  return api.post(url, {
    headers: {
      "X-Dev-Password": password,
    },
  }).json<ClearTableResponse>();
};